import { PatientDocument } from './types';

export interface DocumentGroup {
  kind: string;
  label: string;
  documents: PatientDocument[];
}

/**
 * SQ-167: group the rows by kind, not by form type.
 *
 * The server already collapses `DISCHARGE_REFORM_PT` to kind `DISCHARGE` (SQ-162), so
 * this only has to bucket on a field that is already right — 15 form-type/discipline
 * combinations for one stay become 5 groups a user can actually work with.
 *
 * Groups are ordered largest first: the thing most of the packet is made of should be
 * the first thing read.
 */
export const groupByKind = (documents: PatientDocument[]): DocumentGroup[] => {
  const byKind = new Map<string, DocumentGroup>();

  documents.forEach((doc) => {
    const existing = byKind.get(doc.kind);
    if (existing) {
      existing.documents.push(doc);
    } else {
      byKind.set(doc.kind, { kind: doc.kind, label: doc.kindLabel, documents: [doc] });
    }
  });

  return [...byKind.values()].sort(
    (a, b) => b.documents.length - a.documents.length || a.label.localeCompare(b.label)
  );
};

export type GroupSelectionState = 'all' | 'none' | 'some';

/** Tri-state: a part-selected group shows a dash, never an empty box (D7). */
export const groupSelectionState = (
  group: DocumentGroup,
  selected: Set<string>
): GroupSelectionState => {
  const picked = group.documents.filter((d) => selected.has(d.uuid)).length;
  if (picked === 0) return 'none';
  if (picked === group.documents.length) return 'all';
  return 'some';
};
